import React, { useState } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import {
    useFloating,
    autoUpdate,
    offset,
    flip,
    shift,
    useClick,
    useDismiss,
    useInteractions,
    FloatingPortal,
} from "@floating-ui/react";
import { InfoIcon } from "../StyledComponents/PathwayIcons";
import stringUtils from "../../_services/string-utils";

export const PathwayPopover = (props) => {
    const [open, setOpen] = useState(false);

    const { refs, floatingStyles, context } = useFloating({
        open,
        onOpenChange: setOpen,
        placement: props.placement || "right",
        whileElementsMounted: autoUpdate,
        middleware: [offset(8), flip({ fallbackAxisSideDirection: "end" }), shift({ padding: 8 })],
    });

    const click = useClick(context);
    // Date pickers inside the popover render their calendar in a portal appended to <body>.
    // Without this guard, clicking the calendar triggers "outside press" and closes the popover.
    const dismiss = useDismiss(context, {
        outsidePress: (event) => !event.target.closest(".react-datepicker-popper, .react-datepicker__portal"),
    });
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

    return (
        <>
            <TriggerButton
                ref={refs.setReference}
                id={props.id}
                data-cy={stringUtils.camelCaseToLowerCaseIdentifier(props.id)}
                type="button"
                $primaryColor={props.primaryColor}
                {...getReferenceProps()}
            >
                {props.icon ? (
                    props.icon
                ) : (
                    <InfoIcon $primary={props.primaryColor} $size={props.iconSize || "var(--iconSize-s)"} />
                )}
            </TriggerButton>
            {open && (
                <FloatingPortal>
                    <PopoverPanel
                        ref={refs.setFloating}
                        style={{
                            ...floatingStyles,
                            width: props.width || "400px",
                            maxWidth: props.maxWidth || "400px",
                        }}
                        {...getFloatingProps()}
                    >
                        {props.children}
                    </PopoverPanel>
                </FloatingPortal>
            )}
        </>
    );
};

PathwayPopover.propTypes = {
    id: PropTypes.string.isRequired,
    placement: PropTypes.string,
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    primaryColor: PropTypes.bool,
    maxWidth: PropTypes.string,
    width: PropTypes.string,
    iconSize: PropTypes.string,
    icon: PropTypes.element,
};

const TriggerButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    vertical-align: baseline;
    padding: 0;
    margin: 0;
    background: none;
    border: none;
    cursor: pointer;
    color: inherit;
    line-height: 1;

    ${(props) =>
        props.$primaryColor &&
        css`
            color: var(--pw-primary);
        `}
`;

const PopoverPanel = styled.div`
    z-index: 9999;
    background: var(--surface-card);
    color: var(--text-body);
    font-size: var(--pw-text-sm);
    line-height: var(--pw-leading-relaxed);
    border: 1px solid var(--border-hairline);
    border-radius: var(--pw-radius-xl);
    box-shadow: var(--pw-shadow-lg);
    padding: var(--gap-l);
`;
